import React, { useState } from "react";
import {
  Header,
  Icon,
  Label,
  Divider,
  Statistic,
  Grid,
  Segment,
  Card,
  List,
  Container,
  Popup
} from "semantic-ui-react";
import {
  Chart as ChartJS,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from 'chart.js';
import { Radar } from 'react-chartjs-2';
import { formatDate, sumArray } from "../../constants";
import { useBreakpoint } from "../../hooks/useBreakpoint";
import "../../components/SharedCard.css";
import { CRITERION_ORANGE, evaluatedOutcome, tagLabel, SHOW_CRITERION_EVENT, scrollToWhenReady } from "./evaluationRules";


ChartJS.register(
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

const EvaluationSummary = ({ evaluationsession, hideHeader }) => {
  const { isMobile } = useBreakpoint();
  // Phones show the top 3 of each list and fold 상세 요약 into tap-to-open sections.
  const listLimit = isMobile ? 3 : 5;
  const [expandedLists, setExpandedLists] = useState({});
  const [openSections, setOpenSections] = useState({ 0: true });
  const criterion_set = evaluationsession.evaluatedcriterion_set? 
    evaluationsession.evaluatedcriterion_set : evaluationsession.partnerevaluatedcriterion_set;

  // Helper to find criterion by int_id
  const findCriterion = (int_id) => criterion_set.find(c => c.criterion.int_id === int_id);

  // Process data for Radar Chart (replicating dashboard logic)
  const getTagStats = (criteria) => {
    const stats = {};
    criteria.forEach(c => {
      const tagsString = c.criterion.tags || "기타";
      const tags = tagsString.split(',').map(t => t.trim()).filter(t => t);

      tags.forEach(tag => {
        if (tag === 'deduction') return;

        if (!stats[tag]) {
          stats[tag] = { met: 0, total: 0 };
        }
        stats[tag].total += 1;
        if (evaluatedOutcome(c)?.favorable) {
          stats[tag].met += 1;
        }
      });
    });
    return stats;
  };

  const tagStats = getTagStats(criterion_set);
  const radarLabels = Object.keys(tagStats);
  const radarDataPoints = radarLabels.map(tag => {
      const s = tagStats[tag];
      return (s.met / s.total) * 100;
  });

  const data = {
    labels: radarLabels.map(tagLabel),
    datasets: [
      {
        label: '영역별 성과 (기준 충족 %)',
        data: radarDataPoints,
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 1,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      r: {
        angleLines: {
          display: false
        },
        suggestedMin: 0,
        suggestedMax: 100,
        ticks: {
          stepSize: 20
        }
      }
    },
    plugins: {
      legend: {
        display: false
      }
    }
  };

  // Strengths and Weaknesses
  // An avoided deduction (감점 없음) is neutral: counted as 충족, but not listed as a strength.
  const strengths = criterion_set.filter(c => { const o = evaluatedOutcome(c); return o && o.favorable && !o.deduction; });
  const weaknesses = criterion_set.filter(c => { const o = evaluatedOutcome(c); return o && !o.favorable; });
  const sections = evaluationsession.summary?.sections || [];

  return (
    <div style={{ padding: '0 4px' }}>
      
      {/* Top Stats - Modernized */}
      {!hideHeader && (
      <div style={{ 
        display: 'flex', 
        justifyContent: isMobile ? 'flex-start' : 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        flexDirection: isMobile ? 'column' : 'row',
        marginBottom: '2rem',
        padding: '0 8px',
        borderBottom: '1px solid #f0f0f0',
        paddingBottom: '1.5rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ 
            width: '40px', 
            height: '40px', 
            borderRadius: '50%', 
            backgroundColor: '#F5F5F7', 
            color: '#1d1d1f',
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center',
            fontSize: '18px',
            fontWeight: '600'
          }}>
             {evaluationsession.trainee.full_name ? evaluationsession.trainee.full_name[0] : 'U'}
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f' }}>
              {evaluationsession.trainee.full_name || evaluationsession.trainee.username}
            </div>
            <div style={{ fontSize: '12px', color: '#86868b' }}>
              {formatDate(evaluationsession.created_at)}
            </div>
          </div>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '2rem',
            flexWrap: 'wrap',
            marginTop: isMobile ? '1rem' : 0,
          }}
        >
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#86868b', marginBottom: '4px' }}>충족 항목</div>
            <div style={{ fontSize: '16px', fontWeight: '600', color: '#1d1d1f' }}>
               {criterion_set.filter(c => evaluatedOutcome(c)?.favorable).length} <span style={{ color: '#aaa', fontSize: '13px' }}>/ {criterion_set.length}</span>
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '12px', color: '#86868b', marginBottom: '4px' }}>총점</div>
             <div style={{ fontSize: '16px', fontWeight: '600', color: '#1d1d1f' }}>
                {evaluationsession.total_score} <span style={{ color: '#aaa', fontSize: '13px' }}>/ {sumArray(criterion_set.map((evaluatedcriterion) => evaluatedcriterion.criterion.score))}</span>
             </div>
          </div>
        </div>
      </div>
      )}


      {/* Charts & Lists - Removed Cards */}
      <Grid columns={1} stackable>
        <Grid.Column>
           <div style={{ marginBottom: '2em' }}>
             <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f', marginBottom: '16px' }}>영역별 분석</h3>
             <div style={{ height: '300px', width: '100%', maxWidth: '500px', margin: '0 auto', position: 'relative' }}>
                {radarLabels.length > 0 ? <Radar data={data} options={options} /> : "데이터 없음"}
             </div>
           </div>
        </Grid.Column>
        <Grid.Column>
          <div style={{ marginBottom: '2em' }}>
            <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f', marginBottom: '16px' }}>보완점 & 강점</h3>
            {/* 보완점 first: it's what the trainee acts on next time. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              {[
                ['weak', '보완점', 'thumbs down', '#FF3B30', weaknesses],
                ['strong', '강점', 'thumbs up', '#34C759', strengths],
              ].map(([key, label, icon, color, items]) => {
                const expanded = !!expandedLists[key];
                return (
                  <div key={key}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px', color, fontSize: '13px', fontWeight: '600' }}>
                      <Icon name={icon} /> {label} ({items.length})
                    </div>
                    <List bulleted size="small" style={{ color: '#424245' }}>
                      {(expanded ? items : items.slice(0, listLimit)).map((c, i) => (
                        <List.Item key={i}>{c.criterion.text}</List.Item>
                      ))}
                    </List>
                    {items.length > listLimit && (
                      <button
                        type="button"
                        onClick={() => setExpandedLists(v => ({ ...v, [key]: !expanded }))}
                        style={{ border: 'none', background: 'none', padding: 0, color: '#007AFF', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        {expanded ? '접기' : `${items.length - listLimit}개 더 보기`}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </Grid.Column>
      </Grid>

      <div style={{ marginTop: '1rem', borderTop: '1px solid #f0f0f0', paddingTop: '1.5rem' }}>
        <h3 style={{ fontSize: '15px', fontWeight: '600', color: '#1d1d1f', marginBottom: '1.5rem' }}>상세 요약</h3>
        
        {sections.length > 0 ? (
          sections.map((section, index) => {
            const open = !isMobile || !!openSections[index];
            return (
            <div key={index} style={{ marginBottom: isMobile ? '0.75em' : '2em', borderBottom: isMobile ? '1px solid #f0f0f0' : 'none', paddingBottom: isMobile ? '0.75em' : 0 }}>
              <h4
                onClick={isMobile ? () => setOpenSections(v => ({ ...v, [index]: !open })) : undefined}
                style={{ fontSize: '14px', fontWeight: '600', color: '#1d1d1f', marginBottom: '8px', cursor: isMobile ? 'pointer' : 'default', display: 'flex', alignItems: 'center', gap: '6px' }}
              >
                <span style={{ flex: 1 }}>{section.title}</span>
                {isMobile && <Icon name={open ? 'chevron up' : 'chevron down'} style={{ margin: 0, color: '#c7c7cc', fontSize: '12px' }} />}
              </h4>
              {open && (
              <>
              <p style={{ lineHeight: '1.6', color: '#424245', fontSize: '14px', marginBottom: '12px' }}>
                {section.paragraph}
              </p>
              
              {section.relevant_criteria && section.relevant_criteria.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                   {/* Criteria, not 말풍선: blue is the 말풍선 colour everywhere (bubbles, 관련 말풍선
                       chips), so these use orange — matched by the number on each 응대 스킬 card. */}
                   <span style={{ fontSize: '12px', color: '#86868b', marginRight: '4px' }}>평가 기준:</span>
                   {section.relevant_criteria.map((criterion_int_id) => {
                      const criterion = findCriterion(criterion_int_id);
                      return (
                        <Popup
                          key={criterion_int_id}
                          content={criterion ? criterion.criterion.text : "정보 없음"}
                          size="mini"
                          position="top center"
                          trigger={
                            <span 
                              style={{ 
                                fontSize: '12px', 
                                color: CRITERION_ORANGE, 
                                backgroundColor: '#FFF4E5', 
                                padding: '2px 8px', 
                                borderRadius: '12px', 
                                cursor: 'pointer',
                                fontWeight: '500'
                              }}
                              onClick={() => {
                                const id = `criterion_int_id_${criterion_int_id}`;
                                if (document.getElementById(id)) scrollToWhenReady(id);
                                // Phones filter the criteria list; ask it to show this one.
                                else window.dispatchEvent(new CustomEvent(SHOW_CRITERION_EVENT, { detail: criterion_int_id }));
                              }}
                            >
                              {criterion_int_id + 1}
                            </span>
                          }
                        />
                      );
                    })}
                </div>
              )}
              </>
              )}
            </div>
            );
          })
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#86868b', fontSize: '13px' }}>
              요약 정보가 없습니다.
          </div>
        )}
      </div>
    </div>
  );
};

export default EvaluationSummary;
